import { Menu, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import { StatusIcon } from "../../../../components";
import {
  ACTION_ITEM_STATUSES,
  ActionItemStatus,
  ClosedActionItemStatuses,
  IStatus,
  UserStrategyFilterByOption,
} from "../../../../constants";

interface Props {
  filterByOption: UserStrategyFilterByOption;
  filterByStatusOption: ActionItemStatus | undefined;
  onChange: (filterByOption: UserStrategyFilterByOption, filterByStatusOption: ActionItemStatus | undefined) => void;
}

const UserStrategyFilterToggleButton = (props: Props) => {
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);

  function handleFilterByChanged(value: UserStrategyFilterByOption) {
    // The status filter option should only be set after a status option is selected from the submenu
    if (value != null && value !== UserStrategyFilterByOption.Status) {
      props.onChange(value, undefined);
    }
  }

  function handleFilterByStatusChanged(value: ActionItemStatus) {
    if (value != null) {
      props.onChange(UserStrategyFilterByOption.Status, value);
    }
  }

  return (
    <>
      <ToggleButtonGroup
        value={props.filterByOption}
        exclusive
        orientation="vertical"
        variant="menu"
        onChange={(_, value) => handleFilterByChanged(value)}
        sx={{ minWidth: "100%" }}
      >
        <ToggleButton value={UserStrategyFilterByOption.All}>
          <Typography>All</Typography>
        </ToggleButton>
        <ToggleButton value={UserStrategyFilterByOption.Status} onClick={(e) => setStatusMenuAnchor(e.currentTarget)}>
          <Typography>{`Status ${
            props.filterByStatusOption
              ? `(${ACTION_ITEM_STATUSES.find((s) => s.id === props.filterByStatusOption)?.name})`
              : ""
          }`}</Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        anchorOrigin={{
          horizontal: "left",
          vertical: "top",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <Typography variant="overline" sx={{ mx: 0.5 }}>
          Active Statuses
        </Typography>
        <ToggleButtonGroup
          value={props.filterByStatusOption}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_, value) => handleFilterByStatusChanged(value)}
          sx={{ minWidth: "100%" }}
        >
          {ACTION_ITEM_STATUSES.filter((status) => !ClosedActionItemStatuses.includes(status.id)).map((status) => (
            <StatusToggleButton key={status.id} status={status} />
          ))}
        </ToggleButtonGroup>

        <Typography variant="overline" sx={{ mx: 0.5, pt: 0.75 }}>
          Closed Statuses
        </Typography>
        <ToggleButtonGroup
          value={props.filterByStatusOption}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_, value) => handleFilterByStatusChanged(value)}
          sx={{ minWidth: "100%" }}
        >
          {ACTION_ITEM_STATUSES.filter((status) => ClosedActionItemStatuses.includes(status.id)).map((status) => (
            <StatusToggleButton key={status.id} status={status} />
          ))}
        </ToggleButtonGroup>
      </Menu>
    </>
  );
};

const StatusToggleButton = ({ status }: { status: IStatus }) => {
  return (
    <ToggleButton value={status.id}>
      <Stack direction="row" spacing={0.5} alignItems="baseline">
        <StatusIcon status={status} sx={{ alignSelf: "center" }} />
        <Typography>{status.name}</Typography>
      </Stack>
    </ToggleButton>
  );
};

export { UserStrategyFilterToggleButton };
